import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http/index'
import Notification from 'utils/Notifications'
import {
  Icon,
  PageHeader,
  Card,
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  UnvButton,
  UnvInput,
  UnvModal,
  DataTable,
} from 'components'

import styles from './LessonRescheduleModal.module.scss'

const REASON_HINTS = [
  'Болезнь преподавателя',
  'Перенос группы',
  'Праздничный день',
  'Изменение учебного плана',
  'Аудитория недоступна',
]

const LessonRescheduleModal = () => {
  const { t } = useTranslation('common')
  const [list, setList] = useState([])
  const [lessons, setLessons] = useState([])
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [me, setMe] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ lesson: '', reason: '', new_start: '', room: '' })
  const [rejectOpen, setRejectOpen] = useState(null)
  const [rejectComment, setRejectComment] = useState('')
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const isMethodist = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return roles.includes('methodist') || me.primary_role === 'methodist'
  }, [me])

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/academic/schedule-exceptions/')
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  const loadAux = async () => {
    try { const r = await $api.get('/admin-panel/me/'); setMe(r.data) } catch { setMe(null) }
    try {
      const today = new Date()
      const to = new Date(today)
      to.setDate(to.getDate() + 30)
      const r = await $api.get('/academic/lessons/', {
        params: { date_from: today.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) },
      })
      setLessons(r.data?.results || r.data || [])
    } catch { setLessons([]) }
    try { const r = await $api.get('/academic/rooms/'); setRooms(r.data?.results || r.data || []) } catch { setRooms([]) }
  }

  useEffect(() => { load(); loadAux() }, [])

  const submit = async () => {
    if (!form.lesson || !form.new_start) {
      setNotify({ isOpen: true, message: t('schedule.fill_required', 'Заполните обязательные поля'), type: 'warning', sound: 'warning' })
      return
    }
    try {
      await $api.post('/academic/schedule-exceptions/', form)
      setNotify({ isOpen: true, message: t('schedule.request_sent', 'Заявка отправлена'), type: 'success', sound: 'success' })
      setCreateOpen(false)
      setForm({ lesson: '', reason: '', new_start: '', room: '' })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('common.error', 'Ошибка'), type: 'error', sound: 'error' })
    }
  }

  const approve = async (id) => {
    try {
      await $api.post(`/academic/schedule-exceptions/${id}/approve/`)
      setNotify({ isOpen: true, message: t('schedule.approved_msg', 'Одобрено'), type: 'success', sound: 'success' })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('common.error', 'Ошибка'), type: 'error', sound: 'error' })
    }
  }

  const reject = async () => {
    if (!rejectOpen) return
    try {
      await $api.post(`/academic/schedule-exceptions/${rejectOpen.id}/reject/`, { comment: rejectComment })
      setNotify({ isOpen: true, message: t('schedule.rejected_msg', 'Отклонено'), type: 'success', sound: 'success' })
      setRejectOpen(null)
      setRejectComment('')
      load()
    } catch {
      setNotify({ isOpen: true, message: t('common.error', 'Ошибка'), type: 'error', sound: 'error' })
    }
  }

  const lessonObj = useMemo(() => lessons.find((l) => String(l.id) === String(form.lesson)), [lessons, form.lesson])

  const STATUS = {
    pending: { variant: 'warning', label: t('schedule.status_pending', 'На рассмотрении') },
    approved: { variant: 'success', label: t('schedule.status_approved', 'Одобрено') },
    rejected: { variant: 'danger', label: t('schedule.status_rejected', 'Отклонено') },
  }

  const columns = [
    { key: 'lesson_name', label: t('schedule.col_lesson', 'Занятие'), render: (_, r) => r.lesson_name || r.lesson?.subject_name || `#${r.lesson}` },
    { key: 'old_start', label: t('schedule.col_was', 'Было'), render: (_, r) => r.old_start || r.lesson?.start_at || '—' },
    { key: 'new_start', label: t('schedule.col_will_be', 'Будет'), render: (v) => v || '—' },
    { key: 'reason', label: t('schedule.col_reason', 'Причина') },
    {
      key: 'status', label: t('schedule.col_status', 'Статус'),
      render: (v) => {
        const s = STATUS[v] || { variant: 'muted', label: v }
        return <Badge variant={s.variant}>{s.label}</Badge>
      }
    },
    {
      key: 'actions', label: '',
      render: (_, r) => isMethodist && r.status === 'pending' ? (
        <div className={styles.actionsCol}>
          <UnvButton size="sm" variant="success" iconLeft="check" onClick={() => approve(r.id)}>{t('common.approve', 'Одобрить')}</UnvButton>
          <UnvButton size="sm" variant="danger" iconLeft="x" onClick={() => setRejectOpen(r)}>{t('common.reject', 'Отклонить')}</UnvButton>
        </div>
      ) : null
    },
  ]

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('schedule.reschedule_title', 'Переносы занятий')}
        subtitle={t('schedule.reschedule_subtitle', 'Заявки и согласование изменений')}
        actions={
          <UnvButton variant="primary" iconLeft="plus" onClick={() => setCreateOpen(true)}>
            {t('schedule.new_request', 'Новая заявка')}
          </UnvButton>
        }
      />

      <Card padding="none" className={styles.content}>
        {loading ? (
          <Skeleton variant="table" rows={6} cols={5} />
        ) : error ? (
          <ErrorState title={t('schedule.section_in_dev', 'Раздел в разработке')} onRetry={load} />
        ) : list.length === 0 ? (
          <EmptyState iconName="calendar" title={t('schedule.no_requests', 'Заявок нет')} description="" />
        ) : (
          <DataTable columns={columns} data={list} searchable={false} pageSize={20} />
        )}
      </Card>

      {/* Create Modal */}
      <UnvModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('schedule.new_request', 'Новая заявка на перенос')}
        size="md"
      >
        <div className={styles.form}>
          <label className={styles.lbl}>{t('schedule.original_lesson', 'Исходное занятие')}</label>
          <select
            className={styles.select}
            value={form.lesson}
            onChange={(e) => setForm((f) => ({ ...f, lesson: e.target.value }))}
          >
            <option value="">—</option>
            {lessons.map((l) => (
              <option key={l.id} value={l.id}>
                {(l.date || l.start_at?.slice(0, 10) || '')} · {(l.start_at || '').slice(11, 16)} · {l.subject_name || l.subject || `#${l.id}`}
              </option>
            ))}
          </select>

          {lessonObj && (
            <Card padding="sm" className={styles.summary}>
              <div className={styles.summaryRow}>
                <Icon name="info" size={14} />
                <strong>{t('schedule.summary_title', 'Сводка занятия')}</strong>
              </div>
              <div className={styles.summaryGrid}>
                <span><Icon name="calendar" size={12} /> {lessonObj.date || lessonObj.start_at?.slice(0, 10)}</span>
                <span><Icon name="clock" size={12} /> {(lessonObj.start_at || '').slice(11, 16)}</span>
                <span><Icon name="user" size={12} /> {lessonObj.teacher_name || '—'}</span>
                <span><Icon name="building" size={12} /> {lessonObj.room || '—'}</span>
                <span><Icon name="book" size={12} /> {lessonObj.subject_name || '—'}</span>
                <span><Icon name="group" size={12} /> {lessonObj.group_name || '—'}</span>
              </div>
            </Card>
          )}

          <label className={styles.lbl}>{t('schedule.new_datetime', 'Новая дата/время')}</label>
          <UnvInput
            type="datetime-local"
            value={form.new_start}
            onChange={(e) => setForm((f) => ({ ...f, new_start: e.target.value }))}
          />

          <label className={styles.lbl}>{t('schedule.new_room', 'Новая аудитория')}</label>
          <select
            className={styles.select}
            value={form.room}
            onChange={(e) => setForm((f) => ({ ...f, room: e.target.value }))}
          >
            <option value="">—</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name || r.number}</option>)}
          </select>

          <label className={styles.lbl}>{t('schedule.reason_label', 'Причина переноса')}</label>
          <textarea
            className={styles.textarea}
            rows={3}
            value={form.reason}
            placeholder={t('schedule.reason_placeholder', 'Болезнь преподавателя / Перенос группы / ...')}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
          />
          <div className={styles.hintList}>
            {REASON_HINTS.map((h) => (
              <button
                key={h}
                type="button"
                className={styles.hintChip}
                onClick={() => setForm((f) => ({ ...f, reason: h }))}
              >
                {h}
              </button>
            ))}
          </div>

          {form.new_start && lessonObj && (
            <Card padding="sm" className={styles.preview}>
              <div className={styles.previewHead}>
                <Icon name="arrow-right" size={14} />
                <strong>{t('schedule.preview_title', 'Превью изменения')}</strong>
              </div>
              <div className={styles.previewBody}>
                <span className={styles.was}>
                  {(lessonObj.date || lessonObj.start_at?.slice(0, 10))} · {(lessonObj.start_at || '').slice(11, 16)}
                </span>
                <Icon name="arrow-right" size={14} />
                <span className={styles.will}>
                  {form.new_start.replace('T', ' ')}
                </span>
              </div>
            </Card>
          )}
        </div>
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setCreateOpen(false)}>{t('common.cancel', 'Отмена')}</UnvButton>
          <UnvButton variant="primary" iconLeft="check" onClick={submit}>{t('schedule.send_request', 'Отправить')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      {/* Reject Modal */}
      <UnvModal open={!!rejectOpen} onClose={() => setRejectOpen(null)} title={t('schedule.reject_title', 'Отклонение заявки')} size="sm">
        <textarea
          className={styles.textarea}
          rows={4}
          placeholder={t('schedule.reject_comment', 'Причина отклонения')}
          value={rejectComment}
          onChange={(e) => setRejectComment(e.target.value)}
        />
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setRejectOpen(null)}>{t('common.cancel', 'Отмена')}</UnvButton>
          <UnvButton variant="danger" iconLeft="x" onClick={reject}>{t('common.reject', 'Отклонить')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default LessonRescheduleModal
